<template>
  <div class="play-board" :class="playBoardCss">
    <ul class="play-board__inner style-none" :class="`play-board__inner--${layout}`">
      <li v-for="tile in tiles" :key="tile.tileId" class="tile-list__item">
        <PlayTile
          :answerText="tile.answerText"
          :backgroundColor="tile.backgroundColor"
          :category="category"
          :categoryColor="categoryColor"
          :categoryCssModifier="categoryCssModifier"
          :clicked="tile.clicked"
          :imageFilename="tile.imageFilename"
          :imagesFolderName="imagesFolderName"
          :invertTileImagesInDarkMode="invertTileImagesInDarkMode"
          :mode="mode"
          :numIncorrectClicks="tile.numIncorrectClicks"
          :originalIndex="tile.originalIndex"
          :playState="playState"
          :playStateCssModifier="playStateCssModifier"
          :prompt="tile.prompt"
          :radialGradientHexColor="tile.radialGradientHexColor"
          :tileBackgroundColor="tileBackgroundColor"
          :tileBackgroundPosition="tileBackgroundPosition"
          :tileBackgroundSize="tileBackgroundSize"
          :tileId="tile.tileId"
          :tilePadding="tilePadding"
          :tileRadialGradientHexColor="tileRadialGradientHexColor"
          @tileClicked="tileClicked($event)"
        />
      </li>
    </ul>
  </div>
</template>

<script>
import PlayTile from './PlayTile';
import {BOARD_MODES} from './../constants/globalConstants';
import isAndroid from './../helpers/isAndroid';
import isIOS from './../helpers/isIOS';

export default {
  name: 'PlayBoard',
  components: {
    PlayTile,
  },
  props: {
    category: {
      type: String,
      default: '',
    },
    categoryColor: {
      type: String,
      default: '',
    },
    categoryCssModifier: {
      type: String,
      default: '',
    },
    imagesFolderName: {
      type: String,
      default: '',
    },
    invertTileImagesInDarkMode: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: '',
    },
    playState: {
      type: String,
      default: '',
    },
    playStateCssModifier: {
      type: String,
      default: '',
    },
    tiles: {
      type: Array,
      default: () => [],
    },
    tileBackgroundColor: {
      type: String,
      default: '',
    },
    tileBackgroundPosition: {
      type: String,
      default: '',
    },
    tileBackgroundSize: {
      type: String,
      default: '',
    },
    tilePadding: {
      type: Boolean,
      default: false,
    },
    tileRadialGradientHexColor: {
      type: String,
      default: '',
    },
  },
  data() {
    return {};
  },
  computed: {
    playBoardCss: function() {
      const modeModifierClass = this.mode ? `play-board--${BOARD_MODES[this.mode].CSS_MODIFIER}` : '';
      const iosClass = isIOS() ? 'ios' : '';
      const androidClass = isAndroid() ? 'android' : '';
      return `${modeModifierClass} ${iosClass} ${androidClass}`;
    },
  },
  created() {},
  methods: {
    tileClicked: function(tileId) {
      this.$emit('tileClicked', tileId);
    },
  },
};
</script>

<style lang="scss" scoped>
.play-board {
  background-color: var(--bg-color);
  height: calc(100% - var(--min-play-header-height));
  padding: var(--padding-s);
  padding-bottom: 0;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &.ios {
    /* iOS Safari with minimised UI */
    .play[data-browser-chrome-height='39'] &,
    .play--browser-chrome-height-39 & {
      padding-bottom: 1rem;
    }

    /* iOS Safari with normal UI */
    .play[data-browser-chrome-height='70'] &,
    .play--browser-chrome-height-70 & {
      padding-bottom: 4.5rem;
    }

    /* iOS Android with normal UI */
    .play[data-browser-chrome-height='109'] &,
    .play--browser-chrome-height-109 & {
      padding-bottom: 8rem;
    }
  }

  &.android {
    /* Android Chrome with minimised UI */
    .play[data-browser-chrome-height='39'] &,
    .play--browser-chrome-height-39 & {
      padding-bottom: 1rem;
    }

    /* Android Chrome with normal UI */
    .play[data-browser-chrome-height='95'] &,
    .play--browser-chrome-height-95 & {
      padding-bottom: 7rem;
    }

    /* Android Brave with normal UI */
    .play[data-browser-chrome-height='143'] &,
    .play--browser-chrome-height-95 & {
      padding-bottom: 11rem;
    }

    /* Android Samsung Internet with normal UI */
    .play[data-browser-chrome-height='135'] &,
    .play--browser-chrome-height-95 & {
      padding-bottom: 1rem;
    }
  }
}

.play-board__inner {
  column-gap: 0;
  display: grid;
  height: 100%;
  margin: 0 auto;
  max-width: var(--max-width-play-board);
  row-gap: 0;

  &--1x6 {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--1x7 {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--1x8 {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--2x4 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    } */
  }

  &--1x9 {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--1x10 {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--1x12 {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--2x7 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    } */
  }

  &--2x8 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    } */
  }

  &--2x9 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    } */
  }

  &--2x10 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    } */
  }

  &--2x5 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    } */
  }

  &--2x6 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    } */
  }

  &--3x3 {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
    } */
  }

  &--3x4 {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
    } */
  }

  &--3x5 {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
    } */
  }

  &--5x3 {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--3x6 {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
    } */
  }

  &--6x3 {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--4x3 {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    } */
  }

  &--4x4 {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    } */
  }

  &--4x5 {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    } */
  }

  &--5x4 {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--4x6 {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    } */
  }

  &--6x4 {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--5x5 {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--5x6 {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--6x5 {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }

  &--6x6 {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;

    /* @include breakpoint(orientation portrait) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    @include breakpoint(orientation landscape) {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    } */
  }
}
</style>
